import { collection, doc, getDoc, getDocs, query, runTransaction, setDoc, where } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { app } from "../Components/auth/firebaseConfig";
import { syncTenantLicense } from "../lib/centralTenantApi";
import { deductCreditsFromTenant } from "./tenantPricingModule";

const db = getFirestore(app);

// Helper: Generate acronym from project name
function generateProjectPrefix(name) {
  const words = name.replace(/[^a-zA-Z0-9\s]/g, "").split(/\s+/);
  if (words.length >= 3) {
    return words
      .slice(0, 5)
      .map((w) => w[0])
      .join("")
      .toUpperCase();
  } else if (words.length === 2) {
    return (
      words[0].substring(0, 3).toUpperCase() +
      words[1].substring(0, 2).toUpperCase()
    );
  } else {
    const word = words[0].toUpperCase();
    if (word.length <= 3) return word.padEnd(3, "X");
    if (word.length <= 5) return word;
    return (word.substring(0, 2) + word.slice(-3)).substring(0, 5);
  }
}

// function generateProjectPrefix(projectId, projectName) {
//   const words = projectName.replace(/[^a-zA-Z0-9\s]/g, "").split(/\s+/);
//   let nameComponent = "";
  
//   if (words.length >= 2) {
//     nameComponent = (words[0][0] + words[1][0]).toUpperCase();
//   } else {
//     nameComponent = words[0].substring(0, 2).toUpperCase();
//   }
  
//   // Extract alphanumeric characters from projectId
//   const idComponent = projectId
//     .replace(/[^a-zA-Z0-9]/g, "")
//     .substring(0, 3)
//     .toUpperCase();
  
//   return `${nameComponent}${idComponent}`;
// }

// Transaction-safe increment
export async function getNextCardId(projectId, projectName) {
  const counterRef = doc(db, "counters", projectId);

  const newId = await runTransaction(db, async (tx) => {
    const counterDoc = await tx.get(counterRef);
    let current = 0;

    if (counterDoc.exists()) {
      current = counterDoc.data().lastSerial;
      tx.update(counterRef, { lastSerial: current + 1 });
    } else {
      tx.set(counterRef, { lastSerial: 1 });
      current = 0;
    }

    return current + 1;
  });

  // const prefix = generateProjectPrefix(projectId, projectName);
  // return `${prefix}-${newId}`;
  const prefix = generateProjectPrefix(projectName);
  return `${prefix}-${newId}`;
}

// Save card with generated ID
export async function saveNewCard(projectId, projectName, cardData) {
  if (!cardData?.tenantId) {
    throw new Error("Tenant ID is required to deduct credits for card creation");
  }

  const cardId = await getNextCardId(projectId, projectName);
  const cardRef = doc(db, "cards", cardId);

  await deductTenantCredits(cardData.tenantId, 'addCard', 1, {
    userId: cardData.cardCreatedByUserEmail || cardData.createdByUserEmail || '',
    referenceId: cardId,
  });

  await setDoc(cardRef, {
    cardId,
    ...cardData,
    // Ensure these fields are always present
    cardCreatedByUserEmail: cardData.cardCreatedByUserEmail || cardData.cardUpdatedByUserEmail || cardData.createdByUserEmail || '',
    cardUpdatedByUserEmail: cardData.cardUpdatedByUserEmail || cardData.cardCreatedByUserEmail || cardData.updatedByUserEmail || '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  return cardId;
}

const CREDIT_ACTION_ALIASES = {
  project: 'createProject',
  board: 'createBoard',
  sprint_board: 'createSprintBoard',
  card: 'addCard',
};

function getCreditDeductionErrorMessage(result) {
  if (result?.reason === 'INSUFFICIENT_CREDITS') {
    const required = Number.isFinite(Number(result.requiredCredits))
      ? ` Required: ${result.requiredCredits}.`
      : '';
    const remaining = Number.isFinite(Number(result.remainingCredits))
      ? ` Available: ${result.remainingCredits}.`
      : '';
    return `Not enough credits for this action.${required}${remaining}`;
  }

  if (result?.reason === 'TENANT_INACTIVE') {
    return 'This tenant is inactive. Please activate the tenant before performing this action.';
  }

  if (result?.reason === 'ACTION_NOT_ALLOWED') {
    return 'This action is not available for the current tenant plan.';
  }

  if (result?.reason === 'INVALID_TENANT') {
    return 'Tenant document was not found. Please sync or verify the tenant before trying again.';
  }

  return result?.message || result?.reason || "Unable to deduct credits";
}

function syncTenantToCentralQuietly(tenantId) {
  if (!tenantId) return;
  syncTenantLicense(tenantId).catch((error) => {
    console.warn('[TenantCredit] CCS mirror sync failed after local deduction:', error?.message || error);
  });
}

export async function deductTenantCredits(tenantId, actionType, quantity = 1, metadata = {}) {
  if (!tenantId) {
    throw new Error("Tenant ID is required to deduct credits");
  }

  const action = CREDIT_ACTION_ALIASES[actionType] || actionType;
  const safeQuantity = Math.max(1, Number(quantity) || 1);

  const result = await deductCreditsFromTenant(tenantId, action, safeQuantity, {
    db,
    actor: metadata.userId || metadata.performedBy || metadata.actor || '',
    referenceId: metadata.referenceId || null,
  });

  if (!result.success) {
    const error = new Error(getCreditDeductionErrorMessage(result));
    error.code = result.reason;
    error.reason = result.reason;
    error.requiredCredits = result.requiredCredits;
    error.remainingCredits = result.remainingCredits;
    throw error;
  }

  syncTenantToCentralQuietly(tenantId);

  return {
    ...result,
    referenceId: metadata.referenceId || null,
  };
}

export async function deductTenantCreditsLocally(tenantId, actionType, quantity = 1, metadata = {}) {
  return deductTenantCredits(tenantId, actionType, quantity, metadata);
}

/*
 * Kept only for old imports. OVplan local tenant pricing is the source of truth.
 */
export async function legacyLocalDeductTenantCredits(tenantId, actionType, quantity = 1, metadata = {}) {
  return deductTenantCredits(tenantId, actionType, quantity, metadata);
}

export async function resolveTenantDocIdByTenantId(tenantId) {
  const exactTenantDoc = await getDoc(doc(db, 'tenant', tenantId));
  if (exactTenantDoc.exists()) {
    return exactTenantDoc.id;
  }

  const tenantsQuery = query(
    collection(db, 'tenant'),
    where('tenantId', '==', tenantId)
  );
  const querySnapshot = await getDocs(tenantsQuery);

  if (querySnapshot.empty) {
    throw new Error(`Tenant document not found for tenantId "${tenantId}"`);
  }

  const bestMatch = querySnapshot.docs.find((snap) => {
    const data = snap.data() || {};
    return !!(data.license || data.status || data.companyName || data.tenantName);
  }) || querySnapshot.docs[0];

  return bestMatch.id;
}
